/**
 * **Offline / parse-fallback only.** Portion judgment in production is **LLM-first**
 * (`MealClassifierService` → `portionUnclear` / `portionClarityHint` on validate & create).
 *
 * This module stays sync and shared (backend + MealMap). It does **not** call an LLM.
 * `isPortionUnclear` is a narrow safety net when the model omits `portionUnclear` in JSON
 * (`parseMealClassificationJson`) or the client needs a generic string when the API is unavailable.
 *
 * @see PORTION_CLARITY_STORIES P3.1
 */
/** Generic copy when the API does not return `portionClarityHint` (offline or parse edge). */
export const PORTION_GATE_HINT_FALLBACK = 'Add a measurable amount (oz, g, cups, slices, or counts).';
/**
 * @deprecated Prefer server `portionClarityHint`. Kept for call sites that expect a function.
 */
export function getPortionGateHint(_description) {
    return PORTION_GATE_HINT_FALLBACK;
}
export function isPortionUnclear(description) {
    const t = description.trim();
    if (t.length === 0) {
        return false;
    }
    const normalized = normalizeForPortionHeuristic(t);
    return !hasPortionSizeCueFallback(normalized);
}
/** Minimal regex fallback — prefer classifier output in all product flows. */
function hasPortionSizeCueFallback(text) {
    const s = text.trim();
    // Weight/volume units — allow glued forms (`2Tbsp`) and multi-space (`2  tbsp`).
    if (/\d+(\.\d+)?\s*(oz|ounce|ounces|lb|lbs|pound|pounds|g|grams?|kg|ml|mL|cups?|tbsp|tsp)\b/i.test(s)) {
        return true;
    }
    // ASCII fractions (`1/2`, `1 1/2`) and common unicode fraction glyphs.
    if (/\d+\s+\d+\s*\/\s*\d+/.test(s) ||
        /\d+\s*\/\s*\d+/.test(s) ||
        /[½¼¾⅓⅔⅛⅜⅝⅞]/.test(s)) {
        return true;
    }
    // Acceptable qualitative amounts (not "a lot" / "some" / "plenty").
    if (/\b(?:a\s+few|a\s+couple(?:\s+of)?|couple\s+of|(?:small|large|medium)\s+handfuls?|handfuls?|pinch(?:es)?|dash(?:es)?)\b/i.test(s)) {
        return true;
    }
    // Discrete counts: number/word-number + optional modifiers + a food noun
    // ("6 raspberries", "2 hard boiled eggs", "two bananas").
    const discreteCount = /\b(?:one|two|three|four|five|six|seven|eight|nine|ten|\d+)\s+(?:[\w-]+\s+){0,3}[a-zA-Z][\w-]*\b/i;
    if (discreteCount.test(s)) {
        return true;
    }
    return false;
}
/** Minimal typo fixes for fallback matching only (not a substitute for the LLM). */
const COMMON_FOOD_TYPOS = {
    sandwhich: 'sandwich',
    sandwitch: 'sandwich',
    burgur: 'burger',
    yoghurt: 'yogurt',
};
/**
 * Light normalization before regex fallback. The classifier handles messy NL on save.
 */
export function normalizeForPortionHeuristic(text) {
    return text.replace(/\b[a-zA-Z']+\b/g, word => {
        const lower = word.toLowerCase();
        if (COMMON_FOOD_TYPOS[lower]) {
            return COMMON_FOOD_TYPOS[lower];
        }
        return word;
    });
}
